import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSelector } from "react-redux";
import { selectCurrentRoom } from "@/context/features/room/roomSlice";
import {
  useKickMutation,
  useSetUserStartTimeMutation,
  useGetUpcomingUsersQuery,
} from "@/graphql/generated";
import { toaster } from "@/utils/toaster";
import { RemoteParticipant } from "livekit-client";
import { usePatientTimerContext } from "@/context/features/room/PatientTimerContext";
import ConfirmKick from "./confirm-kick";

interface PatientTimerExpiredDialogProps {
  isAdmin: boolean;
  expiredPatientIdsArray: string[];
}

function PatientTimerExpiredDialog({
  isAdmin,
  expiredPatientIdsArray,
}: PatientTimerExpiredDialogProps) {
  const { t } = useTranslation();
  const { livekitContext } = useLivekitContext();
  const room = livekitContext?.room;
  const { removeExpiredPatient } = usePatientTimerContext();
  const { roomData, focusedVid } = useSelector(selectCurrentRoom);

  const { refetch } = useGetUpcomingUsersQuery({
    variables: { roomName: roomData?.name || "" },
    fetchPolicy: "cache-first",
    skip: !roomData?.name,
  });
  const [open, setOpen] = useState(false);
  const [dismissedExpiredIds, setDismissedExpiredIds] = useState<Set<string>>(
    new Set()
  );
  const [selectedPatient, setSelectedPatient] = useState<string | null>(null);
  const [confirmKickOpen, setConfirmKickOpen] = useState(false);
  const [pendingRemovePatientId, setPendingRemovePatientId] = useState<
    string | null
  >(null);
  const [kick] = useKickMutation();
  const [setStartTime] = useSetUserStartTimeMutation();

  const patient = useMemo(() => {
    if (!room) return null;
    const patients = Array.from(room.remoteParticipants.values()).filter(
      (p: RemoteParticipant) =>
        p.attributes?.roleTag === "patient" || p.attributes?.roleTag === "user"
    );
    return patients.length > 0 ? patients[0] : null;
  }, [room]);

  useEffect(() => {
    if (!isAdmin) return;
    if (confirmKickOpen) return;

    if (expiredPatientIdsArray.length > 0) {
      const firstExpiredId = expiredPatientIdsArray[0];
      const hasNewExpiredPatient = !dismissedExpiredIds.has(firstExpiredId);

      if (hasNewExpiredPatient && !open) {
        // console.log(
        //   "Opening dialog for expired patient:",
        //   firstExpiredId,
        //   "dismissed:",
        //   Array.from(dismissedExpiredIds)
        // );
        setOpen(true);
      }

      setSelectedPatient(firstExpiredId);
    } else if (expiredPatientIdsArray.length === 0 && open) {
      setOpen(false);
      setDismissedExpiredIds(new Set());
      setSelectedPatient(null);
    }
  }, [
    isAdmin,
    expiredPatientIdsArray,
    open,
    dismissedExpiredIds,
    confirmKickOpen,
  ]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen && expiredPatientIdsArray.length > 0) {
      setDismissedExpiredIds(new Set(expiredPatientIdsArray));
    }
  };

  if (!isAdmin) return null;

  const performRemove = async (
    patientId: string,
    info: Record<string, boolean>
  ) => {
    try {
      await kick({
        variables: {
          identity: patientId,
          info: info,
        },
      });
      toaster("info", t("patients_removed"));

      removeExpiredPatient(patientId);
      setDismissedExpiredIds((prev) => {
        const next = new Set(prev);
        next.delete(patientId);
        return next;
      });

      setOpen(false);
      setSelectedPatient(null);
    } catch (e) {
      console.error(e);
      toaster("error", t("error_occurred"));
    }
  };

  const endVisit = () => {
    // console.log("endVisit", { patient, focusedVid });
    if (!patient && !focusedVid) {
      toaster("error", t("error_occurred"));
      return;
    }
    const patientId = patient?.identity.toString() || focusedVid;
    setPendingRemovePatientId(patientId);
    // console.log("patientId", patientId);
    setOpen(false);
    setConfirmKickOpen(true);
  };

  const handleConfirmKick = async (data: Record<string, boolean>) => {
    // console.log("pendingRemovePatientId", pendingRemovePatientId);
    if (pendingRemovePatientId) {
      await performRemove(pendingRemovePatientId, data);
      setPendingRemovePatientId(null);
    }

    setConfirmKickOpen(false);
  };

  const handleConfirmKickOpenChange = (newOpen: boolean) => {
    setConfirmKickOpen(newOpen);
    if (!newOpen && pendingRemovePatientId) {
      setPendingRemovePatientId(null);
    }
  };

  const continueMeet = async () => {
    if (!selectedPatient) {
      setOpen(false);
      if (expiredPatientIdsArray.length > 0) {
        setDismissedExpiredIds(new Set(expiredPatientIdsArray));
      }
      return;
    }
    try {
      const now = Date.now();

      await setStartTime({
        variables: { userId: selectedPatient, startTime: String(now) },
      });

      removeExpiredPatient(selectedPatient);
      setDismissedExpiredIds((prev) => {
        const next = new Set(prev);
        next.delete(selectedPatient);
        return next;
      });

      // toaster("success", t("timer_extended"));
      setOpen(false);
      setSelectedPatient(null);
    } catch (e) {
      console.error(e);
      toaster("error", t("error_occurred"));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-500">
              {t("visit_time_expired")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p>{t("patient_time_expired_desc")}.</p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="destructive"
              onClick={endVisit}
              className="w-full sm:w-auto"
            >
              {t("end_visit")}
            </Button>
            <Button
              variant="default"
              onClick={continueMeet}
              className="w-full sm:w-auto"
            >
              {t("continue_visit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmKick
        open={confirmKickOpen}
        onOpenChange={handleConfirmKickOpenChange}
        confirmCallback={handleConfirmKick}
        refetch={refetch}
      />
    </>
  );
}

export default PatientTimerExpiredDialog;
